import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Trophy, Plus, Flame, Camera, X, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import WinPhotos from "@/components/WinPhotos";
import FollowButton from "@/components/FollowButton";
import AuthorAvatar from "@/components/AuthorAvatar";
import PostActions, { InlinePostEditor } from "@/components/PostActions";
import { PostReactions, AskQuestion, JourneyQnA } from "@/components/CommunityInteractions";

/**
 * WinsFromCircle — community wins feed. Filter state (age group + goals)
 * is controlled by the parent so a single filter bar in the Connection
 * page can drive the entire experience across all three tabs.
 */
export default function WinsFromCircle({
  pulse = 0,
  ageGroup,
  selectedGoals,
  onCheckinPosted,
  hideFilters = false,
}) {
  const [home, setHome] = useState(null);
  const [meta, setMeta] = useState({ age_groups: [], goal_tags: [] });
  // Fallback local state — only used when the component is rendered
  // stand-alone (no controlled props passed). All new usages pass props.
  const [localAgeGroup, setLocalAgeGroup] = useState("all");
  const [localSelectedGoals, setLocalSelectedGoals] = useState([]);
  const effectiveAgeGroup = ageGroup ?? localAgeGroup;
  const effectiveGoals = selectedGoals ?? localSelectedGoals;
  const [wins, setWins] = useState([]);
  const [loadingWins, setLoadingWins] = useState(false);
  const [openCheckin, setOpenCheckin] = useState(false);

  useEffect(() => {
    (async () => {
      const [h, m] = await Promise.all([endpoints.home(), endpoints.meta()]);
      setHome(h.data);
      setMeta(m.data);
      // Seed local (fallback) state from profile only when parent isn't
      // controlling the filters.
      if (ageGroup === undefined) setLocalAgeGroup(h.data.profile.age_group || "all");
      if (selectedGoals === undefined) setLocalSelectedGoals(h.data.profile.longevity_goals || []);
    })();
  }, [pulse]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!home) return;
    setLoadingWins(true);
    endpoints.wins(effectiveAgeGroup, effectiveGoals)
      .then(({ data }) => setWins(data))
      .finally(() => setLoadingWins(false));
  }, [effectiveAgeGroup, effectiveGoals, home]);

  const toggleGoal = (slug) => {
    setLocalSelectedGoals((prev) => (prev.includes(slug) ? prev.filter((g) => g !== slug) : [...prev, slug]));
  };

  const refreshWins = async () => {
    const { data } = await endpoints.wins(effectiveAgeGroup, effectiveGoals);
    setWins(data);
  };

  if (!home) {
    return <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="wins-loading">Steeping…</div>;
  }

  const streak = home.streak;

  return (
    <div className="space-y-10" data-testid="wins-from-circle">
      {/* Filter strip — hidden when the parent (Connection) owns the
          filter bar above the tabs. */}
      {!hideFilters && (
      <section className="space-y-4 reveal" data-testid="filters">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Age group</div>
          <div className="flex flex-wrap gap-2">
            <FilterChip active={effectiveAgeGroup === "all"} onClick={() => setLocalAgeGroup("all")} testid="age-all">All</FilterChip>
            {meta.age_groups.map((a) => (
              <FilterChip key={a} active={effectiveAgeGroup === a} onClick={() => setLocalAgeGroup(a)} testid={`age-${a}`}>{a}</FilterChip>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Goals</div>
          <div className="flex flex-wrap gap-2">
            {meta.goal_tags.map((g) => (
              <FilterChip
                key={g.slug}
                active={effectiveGoals.includes(g.slug)}
                onClick={() => toggleGoal(g.slug)}
                testid={`goal-${g.slug}`}
              >
                {g.name}
              </FilterChip>
            ))}
            {effectiveGoals.length > 0 && (
              <button
                onClick={() => setLocalSelectedGoals([])}
                data-testid="clear-goals"
                className="text-xs text-[hsl(var(--muted-foreground))] underline underline-offset-4 hover:text-[hsl(var(--clay))] ml-2"
              >
                clear
              </button>
            )}
          </div>
        </div>
        <div className="pt-1 flex items-center gap-3 flex-wrap">
          <CheckinDialog
            open={openCheckin}
            onOpenChange={setOpenCheckin}
            defaultGoals={home.profile.longevity_goals || []}
            defaultAgeGroup={home.profile.age_group || "30-40"}
            goalTags={meta.goal_tags}
            ageGroups={meta.age_groups}
            onPosted={async () => { setOpenCheckin(false); await refreshWins(); onCheckinPosted?.(); toast.success("Check-in posted."); }}
          />
          <div className="text-sm px-3 py-1.5 rounded-full bg-[hsl(var(--sand))]" data-testid="wins-streak">
            <Flame size={12} className="inline mr-1 text-[hsl(var(--terracotta))]" />
            <span className="font-display text-base align-middle">{streak}</span>
            <span className="text-[hsl(var(--muted-foreground))]"> day streak</span>
          </div>
        </div>
      </section>
      )}

      {/* Wins Feed — circle wins rise to the top */}
      <section className="reveal reveal-1" data-testid="wins-feed">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-3xl tracking-tight inline-flex items-center gap-2">
            <Trophy size={20} className="text-[hsl(var(--terracotta))]" /> Community wins
          </h2>
          <span className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="wins-count">
            {loadingWins
              ? "listening…"
              : (() => {
                  const c = wins.filter((w) => w.is_followed).length;
                  return c > 0 ? `${wins.length} showing · ${c} from your circle` : `${wins.length} showing`;
                })()}
          </span>
        </div>

        {wins.length === 0 && !loadingWins && (
          <div className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] text-center" data-testid="wins-empty">
            <p className="text-[hsl(var(--muted-foreground))]">
              No wins here yet for your filters. Widen your search — or be the first to post one.
            </p>
          </div>
        )}

        {(() => {
          const circleWins = wins.filter((w) => w.is_followed);
          const otherWins = wins.filter((w) => !w.is_followed);
          const gm = goalMap(meta.goal_tags);
          return (
            <div className="space-y-6">
              {circleWins.length > 0 && (
                <div data-testid="wins-circle-group">
                  <div className="flex items-baseline gap-2 mb-3">
                    <Users size={14} className="text-[hsl(var(--moss))]" />
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--moss))]">
                      From your circle
                    </div>
                    <div className="h-px flex-1 bg-[hsl(var(--moss))]/25 ml-2" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {circleWins.map((w) => (
                      <WinCard key={w.id} post={w} onLike={refreshWins} goalMap={gm} selfAuthor={home.profile.name} />
                    ))}
                  </div>
                </div>
              )}
              {otherWins.length > 0 && (
                <div data-testid="wins-others-group">
                  {circleWins.length > 0 && (
                    <div className="flex items-baseline gap-2 mb-3">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                        Beyond your circle
                      </div>
                      <div className="h-px flex-1 bg-[hsl(var(--border))] ml-2" />
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    {otherWins.map((w) => (
                      <WinCard key={w.id} post={w} onLike={refreshWins} goalMap={gm} selfAuthor={home.profile.name} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </section>
    </div>
  );
}

function goalMap(tags) {
  return Object.fromEntries((tags || []).map((t) => [t.slug, t.name]));
}

export function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 py-1.5 rounded-full text-sm border hairline tab-link ${
        active ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
      }`}
    >
      {children}
    </button>
  );
}

function WinCard({ post, onLike, goalMap, selfAuthor }) {
  const [editing, setEditing] = useState(false);
  const [localPost, setLocalPost] = useState(post);
  useEffect(() => { setLocalPost(post); }, [post]);
  const isOwner = !!(selfAuthor && localPost.author === selfAuthor);
  return (
    <article
      className={`p-6 border rounded-md bg-[hsl(var(--paper))] flex flex-col gap-3 tab-link ${
        post.is_followed ? "border-[hsl(var(--moss))]/60 shadow-[inset_3px_0_0_hsl(var(--moss))]" : "hairline"
      }`}
      data-testid={`win-${post.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <AuthorAvatar
            name={post.author}
            src={post.author_avatar_url}
            size="md"
            testid={`win-avatar-${post.id}`}
          />
          <div className="min-w-0">
            <div className="font-medium text-sm truncate flex items-center gap-2">
              {post.author}
              {post.is_followed && (
                <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]" data-testid={`following-badge-${post.id}`}>
                  Following
                </span>
              )}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
              {post.age_group || "all ages"} · {new Date(post.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <FollowButton
            author={post.author}
            following={!!post.is_followed}
            onChange={() => onLike()}
            selfAuthor={selfAuthor}
            testid={`win-follow-${post.id}`}
          />
          <Trophy size={14} className="text-[hsl(var(--terracotta))]" />
          <PostActions
            post={post}
            isOwner={isOwner}
            editing={editing}
            setEditing={setEditing}
            onChanged={() => { setEditing(false); onLike(); }}
          />
        </div>
      </div>
      {editing ? (
        <InlinePostEditor
          post={post}
          onDone={() => { setEditing(false); onLike(); }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <p className="leading-relaxed">{post.content}</p>
          {post.edited_at && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]" data-testid={`win-edited-${post.id}`}>
              edited
            </div>
          )}
        </>
      )}
      <WinPhotos post={post} size="md" />
      <div className="flex flex-wrap gap-1.5 mt-1">
        {(post.goal_tags || []).map((g) => (
          <span key={g} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-[hsl(var(--sand))] text-[hsl(var(--fg))]">
            {goalMap[g] || g}
          </span>
        ))}
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-1">
        <PostReactions
          post={localPost}
          onChanged={(updated) => setLocalPost((prev) => ({ ...prev, ...updated }))}
        />
        <AskQuestion post={localPost} isCreator={isOwner} />
      </div>
      <JourneyQnA post={localPost} />
    </article>
  );
}

export function CheckinDialog({ open, onOpenChange, defaultGoals, defaultAgeGroup, goalTags, ageGroups, onPosted, mode = "win", hideTrigger = false }) {
  const [content, setContent] = useState("");
  const [goals, setGoals] = useState(defaultGoals);
  const [ag, setAg] = useState(defaultAgeGroup);
  const [posting, setPosting] = useState(false);
  const [beforeFile, setBeforeFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // ``mode`` determines the tone + payload of this dialog. Three modes today:
  //   share    → a general check-in (no is_win, no is_question), photos allowed
  //   win      → celebration/win (is_win: true), photos allowed
  //   question → asking the Collective (is_question: true), photos hidden
  const isQuestion = mode === "question";
  const isWin = mode === "win";
  const modeCopy = {
    win:      { title: "Share a win",         desc: "One line. A before / after if it helps the story land.", placeholder: "Today I…",              cta: "Post the win",     opener: "Post a check-in" },
    share:    { title: "Share",               desc: "A moment, a shift, a small choice worth naming.",       placeholder: "Today I…",              cta: "Share",            opener: "Share" },
    question: { title: "Ask the Collective",  desc: "One clear question the Collective can meet you in.",     placeholder: "What are you wondering about?", cta: "Ask",             opener: "Ask the Collective" },
  }[mode] || {};

  useEffect(() => { setGoals(defaultGoals); }, [defaultGoals, open]);
  useEffect(() => { setAg(defaultAgeGroup); }, [defaultAgeGroup, open]);
  useEffect(() => {
    if (!open) {
      [beforePreview, afterPreview].forEach((p) => p && URL.revokeObjectURL(p));
      setBeforeFile(null); setBeforePreview(null);
      setAfterFile(null); setAfterPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickPhoto = (setFile, setPreview, previewToRevoke) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Only images."); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error("Max 6MB per photo."); return; }
    if (previewToRevoke) URL.revokeObjectURL(previewToRevoke);
    setFile(f);
    setPreview(URL.createObjectURL(f));
    e.target.value = "";
  };

  const clearBefore = () => {
    if (beforePreview) URL.revokeObjectURL(beforePreview);
    setBeforeFile(null); setBeforePreview(null);
  };
  const clearAfter = () => {
    if (afterPreview) URL.revokeObjectURL(afterPreview);
    setAfterFile(null); setAfterPreview(null);
  };

  const submit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      let photo_before_path = null;
      let photo_after_path = null;
      if (beforeFile || afterFile) setUploading(true);
      if (beforeFile) {
        const { data } = await endpoints.uploadPhoto(beforeFile);
        photo_before_path = data.path;
      }
      if (afterFile) {
        const { data } = await endpoints.uploadPhoto(afterFile);
        photo_after_path = data.path;
      }
      setUploading(false);
      const { data: created } = await endpoints.checkin({
        content: content.trim(),
        goal_tags: goals,
        age_group: ag,
        is_win: isWin,
        is_question: isQuestion,
        photo_before_path,
        photo_after_path,
      });
      setContent("");
      clearBefore(); clearAfter();
      onPosted();
      if (created?.moderation_notice) {
        // Soft nudge — never blocks the post.
        toast.warning(created.moderation_notice, { duration: 8000 });
      }
    } catch { toast.error("Could not post."); setUploading(false); }
    setPosting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            data-testid="btn-checkin-open"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
          >
            <Plus size={14} className="mr-2" /> {modeCopy.opener || "Post a check-in"}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-[hsl(var(--paper))] border-[hsl(var(--border))] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="checkin-dialog-title">{modeCopy.title || "Today's check-in"}</DialogTitle>
          <DialogDescription>{modeCopy.desc || "One line. A before / after if it helps the story land."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid="checkin-textarea"
            placeholder={modeCopy.placeholder || "Today I…"}
            className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] min-h-[100px]"
          />

          {!isQuestion && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">
              Becoming photos — then &amp; now (optional)
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="checkin-photo-slots">
              <PhotoSlot
                label="Then"
                preview={beforePreview}
                onPick={pickPhoto(setBeforeFile, setBeforePreview, beforePreview)}
                onClear={clearBefore}
                testid="before"
              />
              <PhotoSlot
                label="Now"
                preview={afterPreview}
                onPick={pickPhoto(setAfterFile, setAfterPreview, afterPreview)}
                onClear={clearAfter}
                testid="after"
              />
            </div>
            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              Attach one or both. Max 6MB each.
            </div>
          </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Your age group</div>
            <div className="flex flex-wrap gap-2">
              {ageGroups.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAg(a)}
                  data-testid={`checkin-age-${a}`}
                  className={`px-3 py-1.5 rounded-full text-sm border hairline ${
                    ag === a ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">Tag goals</div>
            <div className="flex flex-wrap gap-2">
              {goalTags.map((g) => {
                const on = goals.includes(g.slug);
                return (
                  <button
                    key={g.slug}
                    type="button"
                    onClick={() => setGoals((prev) => (on ? prev.filter((x) => x !== g.slug) : [...prev, g.slug]))}
                    data-testid={`checkin-goal-${g.slug}`}
                    className={`px-3 py-1.5 rounded-full text-sm border hairline ${
                      on ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                    }`}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={submit}
            disabled={posting || !content.trim()}
            data-testid="btn-checkin-submit"
            className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
          >
            {uploading ? "Uploading…" : posting ? "Posting…" : (modeCopy.cta || "Post check-in")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoSlot({ label, preview, onPick, onClear, testid }) {
  if (preview) {
    return (
      <div className="relative rounded-md overflow-hidden border hairline aspect-square" data-testid={`checkin-preview-${testid}`}>
        <img src={preview} alt={label} className="w-full h-full object-cover" />
        <span className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/55 text-white backdrop-blur-sm">
          {label}
        </span>
        <button
          type="button"
          onClick={onClear}
          data-testid={`checkin-clear-${testid}`}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
          aria-label={`Remove ${label} photo`}
        >
          <X size={12} />
        </button>
      </div>
    );
  }
  return (
    <label
      data-testid={`checkin-pick-${testid}`}
      className="flex flex-col items-center justify-center gap-2 aspect-square rounded-md border border-dashed hairline cursor-pointer hover:bg-[hsl(var(--sand))] tab-link"
    >
      <Camera size={18} className="text-[hsl(var(--clay))]" />
      <span className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{label}</span>
      <input type="file" accept="image/*" onChange={onPick} data-testid={`checkin-input-${testid}`} className="hidden" />
    </label>
  );
}
